import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';

// Landlord-only "Contacts" list: standalone reusable tenant parties. Listing,
// search and delete live here; create/edit happen on a dedicated page
// (TenantFormView at /tenants/new and /tenants/:id/edit). A tenancy ("Move In")
// is started separately and reuses these records. Search is kept off-state so
// input edits don't re-render and lose focus (same pattern as TenanciesView).
export class TenantsView extends Component {
  constructor() {
    super();
    this.state = { parties: null, error: null };
    this.filters = { search: '' };
  }

  template() {
    const list = this.state.parties;
    return `
      <section class="stack">
        <div class="panel">
          <div class="panel-head panel-head--cta">
            <div class="panel-titles">
              <div class="panel-title-eyebrow">${esc(t('tenants.eyebrow'))}</div>
              <h2 class="panel-title">${esc(t('tenants.title'))}</h2>
            </div>
            <div class="panel-status">
              <a class="btn btn-primary panel-cta" href="#/tenants/new">
                ${icon('user-plus', { size: 'sm' })}<span>${esc(t('tenants.add'))}</span>
              </a>
            </div>
          </div>
        </div>
        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        ${list === null
          ? `<div class="muted">${esc(t('tenants.loading'))}</div>`
          : list.length === 0
            ? `<div class="empty">${esc(t('tenants.empty'))}</div>`
            : `
              <div class="tenancies-toolbar">
                <input class="input" type="search" data-filter="search" placeholder="${esc(t('tenants.search_placeholder'))}" value="${esc(this.filters.search)}" />
              </div>
              <div data-list-region>${this._listHtml(this._apply(list))}</div>
            `}
      </section>
    `;
  }

  _apply(list) {
    const search = this.filters.search.trim().toLowerCase();
    if (!search) return list;
    return list.filter((p) => {
      const hay = [p.display_name, p.email, p.phone, p.tax_number]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(search);
    });
  }

  _listHtml(filtered) {
    if (filtered.length === 0) {
      return `<div class="empty">${esc(t('tenants.no_match'))}</div>`;
    }
    return `<div class="list">${filtered.map((p) => this._itemHtml(p)).join('')}</div>`;
  }

  _itemHtml(p) {
    const sub = [p.email, p.phone].filter(Boolean).join(' · ');
    const isCompany = (p.entity_type || 'individual') === 'company';
    const activeNames = (p.active_property_names || '').split(',').map((s) => s.trim()).filter(Boolean).join(' · ');
    const activeLine = p.active_tenancy_id
      ? `<a class="faint" href="#/tenancies/${esc(p.active_tenancy_id)}">${esc(t('tenants.active_in', { names: activeNames }))}</a>`
      : '';
    return `
      <div class="list-item">
        ${icon('user', { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(p.display_name)}${isCompany ? ` <span class="faint">· ${esc(t('tenants.entity.company'))}</span>` : ''}</strong>
          ${sub ? `<span class="faint">${esc(sub)}</span>` : ''}
          ${activeLine}
        </div>
        <a class="btn btn-ghost btn-sm" href="#/tenancies/new?party=${encodeURIComponent(p.id)}">${icon('user-plus', { size: 'sm' })}<span>${esc(t('tenants.actions.move_in'))}</span></a>
        <a class="btn btn-ghost btn-icon" href="#/tenants/${esc(p.id)}/edit" aria-label="${esc(t('tenants.actions.edit'))}">${icon('pencil', { size: 'sm' })}</a>
        <button class="btn btn-ghost btn-icon" data-action="delete" data-id="${esc(p.id)}" aria-label="${esc(t('tenants.actions.delete'))}">${icon('trash', { size: 'sm' })}</button>
      </div>
    `;
  }

  mount() {
    if (this.state.parties === null) {
      this._load();
      return;
    }
    const root = this.el;
    if (!root) return;

    const search = root.querySelector('[data-filter="search"]');
    if (search) search.addEventListener('input', (e) => {
      this.filters.search = e.target.value;
      this._renderList();
    });

    root.querySelectorAll('[data-action="delete"]').forEach((b) => b.addEventListener('click', () => {
      this._delete(b.getAttribute('data-id'));
    }));
  }

  _renderList() {
    const region = this.el && this.el.querySelector('[data-list-region]');
    if (!region || !Array.isArray(this.state.parties)) return;
    region.innerHTML = this._listHtml(this._apply(this.state.parties));
  }

  async _load() {
    try {
      const res = await api.get('/tenant-parties');
      this.setState({ parties: res.data || [] });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenants.errors.load') });
    }
  }

  async _delete(id) {
    if (!confirm(t('tenants.delete_confirm'))) return;
    try {
      await api.delete(`/tenant-parties/${id}`);
      this.setState({ parties: null });
      this._load();
    } catch (err) {
      const msg = err instanceof ApiError && err.status === 422
        ? t('tenants.errors.delete_in_use')
        : (err instanceof ApiError ? err.message : t('tenants.errors.generic'));
      this.setState({ error: msg });
    }
  }
}
